import { useEffect, useState } from "react";
import { AlertTriangle, Check, Loader2, Monitor, Smartphone, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import ReviewImage from "./ReviewImage";
import { SEVERITY, targetTitle } from "./reviewText";

function BeforeAfter({ rid, t, vp }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {["before", "after"].map((k) => (
        <figure key={k} className="min-w-0">
          <figcaption className={`mb-1 text-[11px] font-medium ${k === "after" ? "text-emerald-700" : "text-slate-500"}`}>{k === "before" ? "Prima" : "Dopo (proposta)"}</figcaption>
          <ReviewImage rid={rid} name={k === "before" ? t.crops?.[vp] : t.after?.[vp]} className={`w-full ${vp === "mobile" ? "max-w-[260px]" : ""} max-h-[420px] object-cover object-top rounded-md border`} testid={`review-${k}-${t.id}-${vp}`} />
        </figure>
      ))}
    </div>
  );
}

function TargetCard({ rid, t, on, setOn, locked }) {
  const [vp, setVp] = useState("desktop");
  return (
    <div className={`rounded-xl border bg-white p-4 ${on && t.ok ? "border-emerald-300" : "border-slate-200"}`} data-testid={`review-proposal-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{targetTitle(t)}</p>
          <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
            {t.defects.map((d) => <li key={d.id}><span className={`mr-1 rounded border px-1 text-[10px] ${SEVERITY[d.severity].cls}`}>{SEVERITY[d.severity].label}</span>{d.fix}</li>)}
          </ul>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded-md border p-0.5">
            {[["desktop", Monitor], ["mobile", Smartphone]].map(([k, I]) => (
              <button key={k} onClick={() => setVp(k)} className={`rounded px-2 py-1 text-xs inline-flex items-center gap-1 ${vp === k ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`review-vp-${k}-${t.id}`}><I size={12} />{k === "desktop" ? "Desktop" : "Mobile"}</button>
            ))}
          </div>
          {t.ok && !locked && (
            <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
              <Switch checked={on} onCheckedChange={setOn} data-testid={`review-approve-${t.id}`} />{on ? "Approvata" : "Scartata"}
            </label>
          )}
        </div>
      </div>
      {!t.ok && <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600"><AlertTriangle size={13} />Correzione non applicabile in modo sicuro: resta com'è.</p>}
      <div className="mt-3"><BeforeAfter rid={rid} t={t} vp={vp} /></div>
    </div>
  );
}

export default function FixStep({ rev, hook }) {
  const fx = rev.fix || {};
  const targets = fx.targets || [];
  const applied = rev.status === "applied";
  const [on, setOn] = useState({});
  const [conflict, setConflict] = useState(null);
  useEffect(() => { setOn(Object.fromEntries(targets.map((t) => [t.id, applied ? (fx.applied || []).includes(t.id) : t.ok]))); }, [rev.id, rev.status, targets.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const approved = targets.filter((t) => t.ok && on[t.id]);
  const undo = async (force) => { const r = await hook.undo(force); if (r && r !== "ok") setConflict(r); };
  return (
    <div className="space-y-3" data-testid="review-fix-step">
      {(fx.warnings || []).length > 0 && (
        <ul className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-0.5" data-testid="review-fix-warnings">{fx.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
      )}
      {targets.map((t) => <TargetCard key={t.id} rid={rev.id} t={t} on={!!on[t.id]} setOn={(v) => setOn((s) => ({ ...s, [t.id]: v }))} locked={applied || rev.status === "undone"} />)}
      <div className="sticky bottom-0 rounded-xl border border-slate-200 bg-white/95 backdrop-blur p-4 flex flex-wrap items-center justify-between gap-3 shadow-sm" data-testid="review-apply-box">
        {rev.status === "ready" && (
          <>
            <p className="text-xs text-slate-600">{approved.length} di {targets.length} correzioni approvate · prima di applicare viene salvata una versione del progetto.</p>
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={!approved.length || hook.busy === "apply"} onClick={() => hook.apply(targets.filter((t) => !on[t.id]).map((t) => t.id))} data-testid="review-apply-btn">
              {hook.busy === "apply" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Check size={14} className="mr-1.5" />}Applica {approved.length} {approved.length === 1 ? "correzione" : "correzioni"}
            </Button>
          </>
        )}
        {applied && (
          <>
            <p className="text-xs text-emerald-700" data-testid="review-applied-note">Applicate {(fx.applied || []).length} correzioni. Rifai gli screenshot per verificarle (gratis).</p>
            <Button variant="outline" onClick={() => undo(false)} disabled={hook.busy === "undo"} data-testid="review-undo-btn"><Undo2 size={14} className="mr-1.5" />Annulla correzioni</Button>
          </>
        )}
        {rev.status === "undone" && <p className="text-xs text-slate-600" data-testid="review-undone-note">Correzioni annullate: il progetto è tornato com'era prima.</p>}
      </div>
      <ConfirmDialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)} destructive confirmLabel="Annulla comunque" title="Elementi modificati dopo le correzioni"
        description={conflict} onConfirm={() => { setConflict(null); undo(true); }} />
    </div>
  );
}
